import { Head } from "vite-react-ssg";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import Breadcrumbs from "@/components/Breadcrumbs";
import RelatedLinks from "@/components/RelatedLinks";
import LastUpdated from "@/components/LastUpdated";
import { DEEPSEEK_PRICING } from "@/lib/deepseek-pricing";
import {
  COMPETITOR_PRICING,
  CHATGPT_CONSUMER_PLANS,
  multipleVs,
  usd,
} from "@/lib/competitor-pricing";

const TITLE = "DeepSeek vs ChatGPT 2026: Prices, Models & Real Specs";
const DESCRIPTION =
  "DeepSeek V4 vs ChatGPT compared on July 2026 prices and specs — V4-Flash from $0.15/M against GPT-5.6 Sol at $5/M. Every figure sourced, none estimated.";
const URL = "https://deepseek.ai/deepseek-vs-chatgpt";

const DeepseekVsChatGPT = () => {
  const flash = DEEPSEEK_PRICING.models["v4-flash"];
  const pro = DEEPSEEK_PRICING.models["v4-pro"];
  const m = COMPETITOR_PRICING.models;

  // Every rate below is read from config; every multiple is computed from
  // those same values, so a config edit can never leave the prose stale.
  const apiRows = [
    { label: flash.label.replace("DeepSeek-", "DeepSeek "), input: flash.inputCacheMiss, cached: flash.inputCacheHit, output: flash.output, highlight: true },
    { label: pro.label.replace("DeepSeek-", "DeepSeek "), input: pro.inputCacheMiss, cached: pro.inputCacheHit, output: pro.output, highlight: true },
    { label: m["gpt-5.6-sol"].label, input: m["gpt-5.6-sol"].input, cached: m["gpt-5.6-sol"].cachedInput, output: m["gpt-5.6-sol"].output, highlight: false },
    { label: m["gpt-5.6-terra"].label, input: m["gpt-5.6-terra"].input, cached: m["gpt-5.6-terra"].cachedInput, output: m["gpt-5.6-terra"].output, highlight: false },
    { label: m["gpt-5.6-luna"].label, input: m["gpt-5.6-luna"].input, cached: m["gpt-5.6-luna"].cachedInput, output: m["gpt-5.6-luna"].output, highlight: false },
    { label: m["gpt-5.5"].label, input: m["gpt-5.5"].input, cached: m["gpt-5.5"].cachedInput, output: m["gpt-5.5"].output, highlight: false },
    { label: m["gpt-5.4"].label, input: m["gpt-5.4"].input, cached: m["gpt-5.4"].cachedInput, output: m["gpt-5.4"].output, highlight: false },
    { label: m["gpt-5.4-mini"].label, input: m["gpt-5.4-mini"].input, cached: m["gpt-5.4-mini"].cachedInput, output: m["gpt-5.4-mini"].output, highlight: false },
    { label: m["gpt-5.4-nano"].label, input: m["gpt-5.4-nano"].input, cached: m["gpt-5.4-nano"].cachedInput, output: m["gpt-5.4-nano"].output, highlight: false },
  ];

  const multiples = [
    {
      comparison: "GPT-5.4 vs V4-Flash",
      input: multipleVs(m["gpt-5.4"].input, flash.inputCacheMiss),
      output: multipleVs(m["gpt-5.4"].output, flash.output),
    },
    {
      comparison: "GPT-5.4 vs V4-Pro",
      input: multipleVs(m["gpt-5.4"].input, pro.inputCacheMiss),
      output: multipleVs(m["gpt-5.4"].output, pro.output),
    },
    {
      comparison: "GPT-5.6 Sol vs V4-Pro",
      input: multipleVs(m["gpt-5.6-sol"].input, pro.inputCacheMiss),
      output: multipleVs(m["gpt-5.6-sol"].output, pro.output),
    },
  ];

  const lowMultiple = multipleVs(m["gpt-5.4"].input, pro.inputCacheMiss);
  const highMultiple = multipleVs(m["gpt-5.4"].output, flash.output);

  const faqItems = [
    {
      question: "Is DeepSeek cheaper than ChatGPT?",
      answer: `On the API, substantially — between ${lowMultiple} and ${highMultiple} depending on which models you compare and whether you are measuring input or output. On consumer subscriptions the comparison does not really apply: DeepSeek's chat is free, ChatGPT's paid tiers buy you capabilities DeepSeek does not offer at all.`,
    },
    {
      question: "Can DeepSeek generate images?",
      answer:
        "No. Both V4 models are text-only. Claims that V4 is natively multimodal circulated before launch, but the models that shipped handle text input and text output. For images you need gpt-image-2 or another provider.",
    },
    {
      question: "Is DeepSeek's context window really 1M tokens?",
      answer:
        "Yes, on both models, with 384K max output. OpenAI's GPT-5.6 family is 1.05M with 128K max output — but priced at roughly double above its long-context threshold, where DeepSeek charges one flat rate.",
    },
    {
      question: "What happened to deepseek-chat and deepseek-reasoner?",
      answer:
        "Retired on 24 July 2026 at 15:59 UTC. Both mapped to deepseek-v4-flash — the first to its non-thinking mode, the second to its thinking mode. Migrating to V4-Pro instead, which looks like the natural successor, triples your bill.",
    },
    {
      question: "Is DeepSeek open source?",
      answer:
        "The weights are published under the MIT licence, so you can self-host. OpenAI's models are API-only.",
    },
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: TITLE,
    description: DESCRIPTION,
    url: URL,
    inLanguage: "en",
    dateModified: COMPETITOR_PRICING.lastVerified,
    isPartOf: {
      "@type": "WebSite",
      name: "The Independent DeepSeek Guide",
      url: "https://deepseek.ai",
    },
    about: [
      { "@type": "Thing", name: "DeepSeek V4" },
      { "@type": "Thing", name: "ChatGPT" },
    ],
  };

  return (
    <>
      <Head>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href={URL} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={URL} />
        <meta property="og:image" content="https://deepseek.ai/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={TITLE} />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image" content="https://deepseek.ai/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Head>

      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
          <Breadcrumbs currentPage="DeepSeek vs ChatGPT" />

          <main>
            <article className="mt-6">
              <header className="mb-10">
                <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-4 leading-tight">
                  DeepSeek vs ChatGPT: What Each One Actually Costs in 2026
                </h1>
                <LastUpdated date={COMPETITOR_PRICING.lastVerified} />
              </header>

              <section className="mb-12">
                <h2 className="text-2xl font-bold text-foreground mb-4">The short answer</h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p>
                    DeepSeek is between 11× and 54× cheaper per token. ChatGPT has the broader
                    product — image generation, voice, video, a consumer app people already use.
                    Whether that gap matters depends entirely on whether you are buying an API or a
                    subscription.
                  </p>
                  <p>
                    If you are building on an API and your workload is text, DeepSeek V4-Flash costs{" "}
                    {usd(flash.inputCacheMiss)} per million input tokens against GPT-5.4's{" "}
                    {usd(m["gpt-5.4"].input)}. That is not a discount, it is a different order of
                    magnitude, and it is the entire reason this comparison exists.
                  </p>
                  <p>
                    If you want an assistant that generates images, holds a voice conversation,
                    browses the web and runs code in a sandbox, DeepSeek does not compete. It ships
                    two text models.
                  </p>
                  <p className="text-sm border-l-2 border-primary/40 pl-4">
                    Last verified 25 July 2026 against DeepSeek's API documentation, OpenAI's
                    developer pricing page and chatgpt.com/pricing. We re-check this page weekly and
                    date every change.
                  </p>
                </div>
              </section>

              <section className="mb-12">
                <h2 className="text-2xl font-bold text-foreground mb-4">
                  What each company actually ships right now
                </h2>

                <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">DeepSeek</h3>
                <p className="text-muted-foreground mb-4">
                  Two models, both text-only, both released 24 April 2026:
                </p>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Model</TableHead>
                        <TableHead>Total params</TableHead>
                        <TableHead>Active per token</TableHead>
                        <TableHead>Context</TableHead>
                        <TableHead>Max output</TableHead>
                        <TableHead>Concurrency</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-medium">deepseek-v4-flash</TableCell>
                        <TableCell>284B</TableCell>
                        <TableCell>~13B</TableCell>
                        <TableCell>1M</TableCell>
                        <TableCell>384K</TableCell>
                        <TableCell>2,500</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">deepseek-v4-pro</TableCell>
                        <TableCell>1.6T</TableCell>
                        <TableCell>~49B</TableCell>
                        <TableCell>1M</TableCell>
                        <TableCell>384K</TableCell>
                        <TableCell>500</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
                <p className="text-muted-foreground mt-4">
                  Mixture-of-experts, MIT licence, open weights. The legacy aliases deepseek-chat and
                  deepseek-reasoner were retired on 24 July 2026 at 15:59 UTC — if your integration
                  still calls them it is broken right now.{" "}
                  <Link
                    to="/blog/deepseek-chat-reasoner-retired-billing-impact"
                    className="text-primary hover:underline font-medium"
                  >
                    See the migration guide →
                  </Link>
                </p>

                <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">OpenAI</h3>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Model</TableHead>
                        <TableHead>Context</TableHead>
                        <TableHead>Max output</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {["gpt-5.6-sol", "gpt-5.6-terra", "gpt-5.6-luna"].map((id) => (
                        <TableRow key={id}>
                          <TableCell className="font-medium">{id}</TableCell>
                          <TableCell>1.05M</TableCell>
                          <TableCell>128K</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <p className="text-muted-foreground mt-4">
                  All three support vision, function calling, web search, file search and computer
                  use. Alongside them OpenAI ships gpt-image-2 for images, the gpt-realtime-2.1
                  family for audio, sora-2 for video and gpt-5.3-codex for coding.
                </p>
                <p className="text-muted-foreground mt-4">
                  That breadth is the real difference. DeepSeek gives you text at a low price. OpenAI
                  gives you a platform.
                </p>
              </section>

              <section className="mb-12">
                <h2 className="text-2xl font-bold text-foreground mb-4">API pricing, side by side</h2>
                <p className="text-muted-foreground mb-4">
                  Per 1M tokens, standard tier, short context:
                </p>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Model</TableHead>
                        <TableHead>Input</TableHead>
                        <TableHead>Cached input</TableHead>
                        <TableHead>Output</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {apiRows.map((r) => (
                        <TableRow key={r.label} className={r.highlight ? "bg-primary/5" : undefined}>
                          <TableCell className="font-medium">{r.label}</TableCell>
                          <TableCell>{usd(r.input)}</TableCell>
                          <TableCell>{r.cached === null ? "—" : usd(r.cached)}</TableCell>
                          <TableCell>{usd(r.output)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <p className="text-muted-foreground mt-6">
                  Two things worth knowing that most comparisons miss.
                </p>
                <p className="text-muted-foreground mt-4">
                  <strong className="text-foreground">
                    OpenAI charges roughly double above a context threshold.
                  </strong>{" "}
                  The pricing page splits every flagship model into "short context" and "long
                  context" columns — GPT-5.6 Sol goes from {usd(m["gpt-5.6-sol"].input)}/
                  {usd(m["gpt-5.6-sol"].output)} to {usd(m["gpt-5.6-sol"].longContext.input)}/
                  {usd(m["gpt-5.6-sol"].longContext.output)}. If you are working with long documents,
                  the headline price is not the price you pay. DeepSeek charges one rate across the
                  full 1M window.
                </p>
                <p className="text-muted-foreground mt-4">
                  <strong className="text-foreground">
                    Cache-hit pricing is where the gap is widest.
                  </strong>{" "}
                  DeepSeek's cache-hit input is {usd(flash.inputCacheHit)} for Flash —{" "}
                  {Math.round((flash.inputCacheHit / flash.inputCacheMiss) * 100)}% of the cache-miss
                  rate. OpenAI's cached input is{" "}
                  {Math.round((m["gpt-5.6-sol"].cachedInput! / m["gpt-5.6-sol"].input) * 100)}% of
                  standard. On a workload with heavy prompt reuse, the effective difference is far
                  larger than the headline suggests.
                </p>

                <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
                  The multiples, spelled out
                </h3>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Comparison</TableHead>
                        <TableHead>Input</TableHead>
                        <TableHead>Output</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {multiples.map((r) => (
                        <TableRow key={r.comparison}>
                          <TableCell className="font-medium">{r.comparison}</TableCell>
                          <TableCell>{r.input} more</TableCell>
                          <TableCell>{r.output} more</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <p className="text-muted-foreground mt-4">
                  Full per-token breakdown and a cost calculator on our{" "}
                  <Link to="/pricing" className="text-primary hover:underline font-medium">
                    DeepSeek pricing page
                  </Link>
                  , and the architecture detail on{" "}
                  <Link to="/deepseek-v4" className="text-primary hover:underline font-medium">
                    DeepSeek V4 explained
                  </Link>
                  .
                </p>
              </section>

              <section className="mb-12">
                <h2 className="text-2xl font-bold text-foreground mb-4">Consumer plans</h2>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>ChatGPT plan</TableHead>
                        <TableHead>Price (US)</TableHead>
                        <TableHead>Notes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {CHATGPT_CONSUMER_PLANS.plans.map((p) => (
                        <TableRow key={p.name}>
                          <TableCell className="font-medium">{p.name}</TableCell>
                          <TableCell>{p.price}</TableCell>
                          <TableCell className="text-muted-foreground">{p.notes}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <p className="text-muted-foreground mt-4">
                  DeepSeek's consumer side is chat.deepseek.com — free, but it requires an account.
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  {CHATGPT_CONSUMER_PLANS.footnote}
                </p>
              </section>

              <section className="mb-12">
                <h2 className="text-2xl font-bold text-foreground mb-4">
                  Where each one genuinely wins
                </h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p>
                    <strong className="text-foreground">Choose DeepSeek</strong> when you are running
                    text at volume, cost per token is the binding constraint, you want open weights
                    you can self-host, or you need a 1M context window without a long-context
                    surcharge.
                  </p>
                  <p>
                    <strong className="text-foreground">Choose ChatGPT</strong> when you need images,
                    audio, video or browsing; when you want one vendor for the whole stack; when your
                    team needs a polished consumer app; or when enterprise controls, SSO and
                    data-residency guarantees are procurement requirements.
                  </p>
                  <p>
                    The honest framing: these are not competing for the same buyer. DeepSeek competes
                    on price per token for text. OpenAI competes on being the default. Most teams
                    that pick DeepSeek do so for a specific high-volume workload, not to replace
                    ChatGPT.
                  </p>
                </div>
              </section>

              <section className="mb-12">
                <h2 className="text-2xl font-bold text-foreground mb-4">
                  Why this page has no benchmark table
                </h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p>
                    Almost every DeepSeek-versus-ChatGPT comparison online carries a table of
                    SWE-bench, MMLU and HumanEval scores. We checked those figures and most of them
                    cannot be traced to the vendor that supposedly published them, or to any named
                    leaderboard.
                  </p>
                  <p>
                    We would rather tell you where to check than print a number we cannot stand
                    behind:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>
                      Artificial Analysis publishes dated, methodology-documented leaderboards. On
                      GPQA Diamond it currently has GPT-5.6 Sol at 94.1% and GPT-5.5 (xhigh) at
                      93.5%.
                    </li>
                    <li>
                      DeepSeek publishes its own comparison table on the V4-Pro model card. Note that
                      it benchmarks against Opus-4.6 Max, GPT-5.4 xHigh and Gemini-3.1-Pro High — the
                      models current at V4's April release, not today's.
                    </li>
                    <li>OpenAI publishes model-specific evaluations in its release posts.</li>
                  </ul>
                  <p>
                    If a comparison page shows you a benchmark score with no link and no date, treat
                    it as decoration.
                  </p>
                </div>
              </section>

              <section className="mb-12">
                <h2 className="text-2xl font-bold text-foreground mb-4">FAQ</h2>
                <Accordion type="single" collapsible className="w-full">
                  {faqItems.map((f, i) => (
                    <AccordionItem key={i} value={`faq-${i}`}>
                      <AccordionTrigger className="text-left font-medium">
                        {f.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground leading-relaxed">
                        {f.answer}
                        {f.question.startsWith("What happened") && (
                          <>
                            {" "}
                            <Link
                              to="/blog/deepseek-chat-reasoner-retired-billing-impact"
                              className="text-primary hover:underline font-medium"
                            >
                              Full migration guide →
                            </Link>
                          </>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </section>

              <RelatedLinks />
            </article>
          </main>
        </div>
      </div>
    </>
  );
};

export default DeepseekVsChatGPT;
